import { useState } from "react";
import { ChevronDown, ChevronRight, FileUp, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errMsg } from "@/lib/api";
import { Panel, Th, Td, btn } from "@/components/ui-kit";
import { Header, Lines, Checks, gbp } from "@/components/sap/AmazonPoPanel";
import { FixButton } from "@/components/sap/FixDialog";
import { ViewPoButton } from "@/components/ViewPoButton";

const state = (d) => (d.result ? "done" : d.err ? "unreadable" : d.failed ? "failed" : d.stale ? "recheck" : d.g.blocking.length ? "stopped" : d.g.warnings.length && !d.ack ? "tick" : "ready");
const LABEL = { done: ["text-green-300", "Created"], unreadable: ["text-rose-300", "Not read"], failed: ["text-rose-300", "Failed"], recheck: ["text-amber-300", "Re-check"], stopped: ["text-rose-300", "Stopped"], tick: ["text-amber-300", "Needs tick"], ready: ["text-green-300", "Ready"] };

const withBatchDupes = (list) => {
  const seen = {};
  return list.map((d) => {
    if (!d.g || d.result) return d;
    const po = d.g.po_number;
    const dup = seen[po];
    seen[po] = true;
    const msg = `PO ${po} is also in another file in this batch`;
    const blocking = d.g.blocking.filter((b) => b !== msg);
    return { ...d, g: { ...d.g, blocking: dup ? [...blocking, msg] : blocking } };
  });
};

const Detail = ({ d, upd, recheck }) => (
  <div className="border-t border-slate-800 bg-[#17191F]/40" data-testid={`amz-detail-${d.key}`}>
    <Header g={d.g} />
    <Lines g={d.g} setPallets={(i, v) => upd({ stale: true, g: { ...d.g, lines: d.g.lines.map((l, j) => (j === i ? { ...l, pallets: v === "" ? "" : Number(v) } : l)) } })} />
    <Checks g={d.g} ack={d.ack} setAck={(v) => upd({ ack: v, pick: v || d.pick })} />
    <div className="flex flex-wrap gap-2 px-5 pb-3"><Button size="sm" variant="ghost" onClick={recheck} className={btn.ghost} data-testid={`amz-recheck-${d.key}`}><RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Re-check</Button><ViewPoButton fid={d.g.po_file_id} small testId={`amz-view-po-${d.key}`} /><FixButton fix={d.g.fix && { ...d.g.fix, source: `Amazon PO ${d.g.po_number}` }} onDone={recheck} small testId={`amz-fix-${d.key}`} /></div>
  </div>
);

const Row = ({ d, upd, remove, recheck }) => {
  const s = state(d);
  const [cls, txt] = LABEL[s];
  return (
    <>
      <tr data-testid={`amz-row-${d.key}`}>
        <Td><Checkbox checked={!!d.pick && s === "ready"} disabled={s !== "ready"} onCheckedChange={(v) => upd({ pick: !!v })} data-testid={`amz-pick-${d.key}`} /></Td>
        <Td><button onClick={() => upd({ open: !d.open })} disabled={!d.g} className="flex items-center gap-1 text-left" data-testid={`amz-toggle-${d.key}`}>{d.g && (d.open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />)}<span className="font-mono">{d.g?.po_number || d.name}</span></button>{d.g && <div className="text-xs text-slate-500">{d.name}</div>}</Td>
        <Td className="font-mono">{d.g?.fc || "-"}</Td><Td className="font-mono">{d.g ? d.g.total_pallets : "-"}</Td><Td>{d.g?.price_break || "-"}</Td><Td className="font-mono">{d.g ? gbp(d.g.value) : "-"}</Td>
        <Td><span className={`text-xs font-semibold ${cls}`} data-testid={`amz-state-${d.key}`}>{txt}</span>
          <div className="text-xs text-slate-500">{d.result ? (d.result.idoc_file || `IDoc not made: ${d.result.error}`) : d.err || d.failed || d.g?.blocking[0] || d.g?.warnings[0] || ""}</div></Td>
        <Td>{!d.result && <Button size="sm" variant="ghost" onClick={remove} className={btn.danger} data-testid={`amz-remove-${d.key}`}><Trash2 className="h-3.5 w-3.5" /></Button>}</Td>
      </tr>
      {d.open && d.g && <tr><td colSpan={8} className="p-0"><Detail d={d} upd={upd} recheck={recheck} /></td></tr>}
    </>
  );
};

const notes = (g) => [g.booking_ref && `Booking ref ${g.booking_ref}`, g.delivery_time && `Del time ${g.delivery_time}`, g.contact && `Contact ${g.contact}`].filter(Boolean).join("; ");
export const sapTexts = (g) => ({ supplier_note: g.supplier_note || "", booking_ref: g.booking_ref || "", delivery_slot: [g.delivery_date && `Delivery Date: ${g.delivery_date}`, g.delivery_time && `Delivery Time: ${g.delivery_time}`].filter(Boolean).join(" ") });

export const AmazonBulkPanel = () => {
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(false);
  const upd = (key, patch) => setList((l) => l.map((d) => (d.key === key ? { ...d, ...patch } : d)));
  const read = async (f) => {
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/amazon/po-pdf", fd);
      return { g: data, pick: !data.blocking.length && !data.warnings.length };
    } catch (e) {
      return { err: errMsg(e) };
    }
  };
  const readEmail = async (f) => {
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/amazon/po-email", fd);
      return data.results.map((r) => ({ name: `${f.name} › ${r.name}`, ...(r.g ? { g: r.g, pick: !r.g.blocking.length && !r.g.warnings.length } : { err: r.err }) }));
    } catch (e) {
      return [{ name: f.name, err: errMsg(e) }];
    }
  };
  const addFiles = async (files) => {
    files = files.filter((f) => /\.(pdf|eml)$/i.test(f.name));
    if (!files.length) return toast.error("Drop Amazon PO PDFs or saved emails (.eml)");
    setBusy(true);
    const rows = [];
    for (const f of files) {
      const got = /\.eml$/i.test(f.name) ? await readEmail(f) : [{ name: f.name, ...(await read(f)) }];
      got.forEach((r) => rows.push({ key: `${Date.now()}-${rows.length}-${r.name}`, ...r }));
    }
    setList((l) => withBatchDupes([...l.filter((d) => !d.result), ...rows]));
    setBusy(false);
    const bad = rows.filter((r) => r.err).length;
    bad ? toast.warning(`${rows.length - bad} of ${rows.length} PO(s) read - ${bad} could not be read`) : toast.success(`${rows.length} PO(s) read`);
  };
  const upload = (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = "";
    addFiles(files);
  };
  const [over, setOver] = useState(false);
  const drop = (e) => {
    e.preventDefault();
    setOver(false);
    addFiles([...(e.dataTransfer.files || [])]);
  };
  const recheck = async (d) => {
    try {
      const { data } = await api.post("/amazon/check", { ...d.g, lines: d.g.lines.map(({ flags, ...l }) => l) });
      setList((l) => withBatchDupes(l.map((x) => (x.key === d.key ? { ...x, g: data, failed: "", stale: false, ack: false, pick: !data.blocking.length && !data.warnings.length } : x))));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const picked = list.filter((d) => d.pick && state(d) === "ready");
  const approve = async () => {
    setBusy(true);
    let ok = 0;
    for (const d of picked) {
      const g = d.g;
      try {
        const { data } = await api.post("/manual-orders", { partner: "AMAZON", po_number: g.po_number, po_date: g.po_date, delivery_date: g.delivery_date, depot: g.depot, notes: notes(g), ...sapTexts(g), source: "Amazon PO PDF", po_file_id: g.po_file_id || "", lines: g.lines.map((l) => ({ item: l.part_no, qty: l.pallets })) });
        upd(d.key, { result: data, open: false, pick: false });
        ok += 1;
      } catch (e) {
        upd(d.key, { failed: errMsg(e), pick: false });
      }
    }
    setBusy(false);
    (ok === picked.length ? toast.success : toast.warning)(`${ok} of ${picked.length} order(s) created with SAP IDocs`);
  };
  const selectAll = () => setList((l) => l.map((d) => ({ ...d, pick: state(d) === "ready" })));
  return (
    <div onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)} onDrop={drop} className={over ? "rounded-sm ring-2 ring-emerald-500" : ""} data-testid="amz-drop-zone">
    <Panel title="Amazon POs (PDF or email) - upload and check" testId="amz-po-panel"
      action={<label className={`inline-flex h-8 cursor-pointer items-center rounded-md px-3 text-xs ${btn.ghost}`} data-testid="amz-upload-label"><FileUp className="mr-1.5 h-3.5 w-3.5" /> {busy ? "Working…" : "Upload PO PDFs or emails"}<input type="file" accept=".pdf,.eml" multiple className="hidden" onChange={upload} disabled={busy} data-testid="amz-upload-input" /></label>}>
      {!list.length ? <p className="px-5 py-4 text-xs text-slate-500" data-testid="amz-empty">Drag Amazon PO PDFs or saved Amazon PO emails (.eml - in Outlook drag the email to your desktop, or File › Save As) onto this box, or click Upload. The PDF is taken from each email automatically. Each PO is checked: EACH converted to pallets, price break from the total pallets on the PO, FC to SAP ship-to, prices compared with Amazon's. Open a PO to fix pallets or tick its warnings, then approve the ticked ones together.</p> : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-slate-800"><tr><Th></Th><Th>PO / file</Th><Th>FC</Th><Th>Pallets</Th><Th>Break</Th><Th>Value</Th><Th>Status</Th><Th></Th></tr></thead>
              <tbody className="divide-y divide-slate-800/70">
                {list.map((d) => <Row key={d.key} d={d} upd={(p) => upd(d.key, p)} remove={() => setList((l) => withBatchDupes(l.filter((x) => x.key !== d.key)))} recheck={() => recheck(d)} />)}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap items-center gap-2 border-t border-slate-800 px-5 py-3">
            <Button variant="ghost" onClick={selectAll} className={btn.ghost} data-testid="amz-select-ready-btn">Tick all ready</Button>
            <Button variant="ghost" onClick={() => setList([])} className={btn.ghost} data-testid="amz-clear-btn">Clear list</Button>
            <span className="text-xs text-slate-500" data-testid="amz-summary">{list.filter((d) => state(d) === "ready").length} ready · {list.filter((d) => ["stopped", "unreadable"].includes(state(d))).length} stopped · {list.filter((d) => ["tick", "recheck"].includes(state(d))).length} need attention · {list.filter((d) => d.result).length} created</span>
            <Button onClick={approve} disabled={busy || !picked.length} className={`ml-auto ${btn.primary}`} data-testid="amz-approve-btn">Approve {picked.length} ticked and create SAP IDocs</Button>
          </div>
        </>
      )}
    </Panel>
    </div>
  );
};
